import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import OvRecordEditDialog from '../molecules/OvRecordEditDialog';
import OvAddVideoPlacementForm from './OvAddVideoPlacementForm';
import { VideoPlacement } from '../../../common/model/dto/video/video-placement';
import { NewPlacementValues } from '../../../common/model/type/video-placement.types';
import {
  placementDescriptionError,
  placementKeyError,
} from '../../../common/utils/video-placement-utils';
import { getAxiosErrorMessage } from '../../../common/utils/helpers';
import { getHttpStatus } from '../../../common/utils/http-status';
import { useVideoPlacementMutations } from '../../../hooks/use-video-placement-mutations';

interface OvAddVideoPlacementDialogProps {
  isOpen: boolean;
  placements: VideoPlacement[];
  onAdded?: (placement: VideoPlacement) => void;
  onClose: () => void;
}

const INITIAL_VALUES: NewPlacementValues = { key: '', description: '' };

const OvAddVideoPlacementDialog: FC<OvAddVideoPlacementDialogProps> = ({
  isOpen,
  placements,
  onAdded,
  onClose,
}) => {
  const { t } = useTranslation();
  const { create } = useVideoPlacementMutations();

  const formik = useFormik<NewPlacementValues>({
    initialValues: INITIAL_VALUES,
    onSubmit: ({ key, description }) =>
      create.mutate(
        { key, description: description.trim() },
        {
          onSuccess: (placement) => {
            onAdded?.(placement);
            close();
          },
        }
      ),
  });

  const close = () => {
    formik.resetForm();
    create.reset();
    onClose();
  };

  const { key, description } = formik.values;
  const isConflict =
    getHttpStatus(create.error) === 409 && create.variables?.key === key;
  const keyError = isConflict ? 'exists' : placementKeyError(key, placements);
  const descriptionError = placementDescriptionError(description);

  return (
    <OvRecordEditDialog
      isOpen={isOpen}
      title={t('videos.placements.add.title')}
      submitLabel={t('videos.placements.add.submit')}
      busyLabel={t('videos.placements.add.busy')}
      isDirty={formik.dirty}
      isSaving={create.isPending}
      isSubmitDisabled={!!keyError || !!descriptionError}
      errorMessage={
        create.error && !isConflict
          ? getAxiosErrorMessage(create.error)
          : undefined
      }
      onSubmit={formik.submitForm}
      onClose={close}
    >
      <OvAddVideoPlacementForm
        formik={formik}
        keyError={keyError}
        descriptionError={descriptionError}
      />
    </OvRecordEditDialog>
  );
};

export default OvAddVideoPlacementDialog;
